import { Buffer } from 'buffer';

export type ResumoDia = { pedidos: number; faturamento: number; ticketMedio: number };
type Venda = { pk?: string; sk?: string; gsi2sk?: string; valorTotal?: string | number };
export const temAcesso = (valor: unknown) => valor === 'leitura' || valor === 'edicao';
export const podeVerResumo = (acessos?: Record<string, unknown> | null) => temAcesso(acessos?.desempenho);

export function dataBrasilia(agora = new Date()): string {
  const partes = new Intl.DateTimeFormat('en-US', { timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(agora);
  const valor = (tipo: string) => partes.find(parte => parte.type === tipo)?.value;
  return `${valor('year')}-${valor('month')}-${valor('day')}`;
}

export function calcularResumoDia(vendas: Venda[], dia: string): ResumoDia {
  const vistos = new Set<string>();
  let centavos = 0;
  let pedidos = 0;
  for (const venda of vendas) {
    // O relatório usa a data da venda no índice, não a data de encerramento.
    // BETWEEN inclui o limite final; excluímos vendas da meia-noite do dia seguinte.
    const dataIndice = venda.gsi2sk?.split('#Encerrado#')[1];
    if (dataIndice && dataBrasilia(new Date(dataIndice)) !== dia) continue;
    const chave = venda.pk && venda.sk ? JSON.stringify([venda.pk, venda.sk]) : undefined;
    if (chave && vistos.has(chave)) continue;
    if (chave) vistos.add(chave);
    const total = typeof venda.valorTotal === 'number' ? venda.valorTotal
      : Number((venda.valorTotal || '').replace(/\s|R\$/g, '').replace(/\.(?=.*[,])/g, '').replace(',', '.'));
    if (venda.valorTotal === undefined || venda.valorTotal === '' || !Number.isFinite(total)) {
      throw new Error('Venda sem valor total válido');
    }
    // valorTotal já contém descontos/taxas e não contém o troco. Pagamentos
    // divididos não devem duplicar a venda nem alterar seu valor líquido.
    centavos += Math.round(total * 100);
    pedidos += 1;
  }
  return { pedidos, faturamento: centavos / 100, ticketMedio: pedidos ? centavos / 100 / pedidos : 0 };
}

export async function carregarResumoDia(baseUrl: string, loja: string, token: string, dia: string, signal: AbortSignal): Promise<ResumoDia> {
  const canais = ['', 'caixa', 'comandas', 'totem'];
  const resultados = await Promise.all(canais.map(async canal => {
    const query = new URLSearchParams({ campo: 'data', valor: dia, valor2: dia, filtro: 'Encerrado', lojas: Buffer.from(loja).toString('base64') });
    if (canal) query.set('filtro2', canal);
    // O endpoint percorre todas as páginas do DynamoDB antes de retornar Items.
    const resposta = await fetch(`${baseUrl}/ordens/${canal ? 'relatoriocc' : 'relatorio'}?${query}`, {
      method: 'GET', headers: { token }, credentials: 'include', signal,
    });
    if (!resposta.ok) throw new Error('Não foi possível carregar o resumo');
    const dados = await resposta.json();
    if (!Array.isArray(dados.Items)) throw new Error('Resumo indisponível');
    return dados.Items as Venda[];
  }));
  return calcularResumoDia(resultados.flat(), dia);
}
